import { SearchOutlined } from '@ant-design/icons';
import {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import keymap from '@/utils/keymap';

import { BUTTON_SIZE } from '../Button/IconButton/styled';
import { HeightSize } from './Input/types';

type SearchBarStatusT = 'shrinked' | 'expanded';
type SearchInputT = {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onClearInput?: (el?: HTMLInputElement) => void;
  shrink?: boolean;
  rounded?: boolean;
  placeholder: string;
  width?: string;
  initialValue?: string;
  css?: StyledCss;
  sizeType?: SizeType;
  hasValue?: boolean;
};

export type ExpandSearchInputHandle = {
  initInputValue: () => void;
};

const ExpandSearchInput = forwardRef<ExpandSearchInputHandle, SearchInputT>(
  (props, ref) => {
    const { sizeType = 'l' } = props;

    useImperativeHandle(ref, () => ({
      initInputValue,
    }));

    const [inputValue, setInputValue] = useState(props.initialValue);
    const [clearActive, setClearActive] = useState(false);
    const [searchBarStatus, setSearchBarStatus] = useState<SearchBarStatusT>(
      props.shrink ? 'shrinked' : 'expanded',
    );
    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnFocusInput = (e: FocusEvent<HTMLInputElement>) => {
      if (props.shrink) {
        setSearchBarStatus('expanded');
      }

      props.onFocus?.(e);
    };

    const handleOnBlurInput = (e: FocusEvent<HTMLInputElement>) => {
      // to prevent blur and click search button event conflict
      setTimeout(() => {
        if (
          !clearActive &&
          !e.target.value &&
          props.shrink &&
          searchBarStatus === 'expanded'
        ) {
          setSearchBarStatus('shrinked');
        }
      }, 100);
      props.onBlur?.(e);
    };

    const onInputValueChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      setInputValue(value);
    };

    const initInputValue = () => {
      setInputValue('');
    };

    const onClickClear = () => {
      initInputValue();
      setClearActive(false);

      if (props.shrink && searchBarStatus === 'expanded') {
        setSearchBarStatus('shrinked');
      }

      if (inputRef.current) {
        props.onClearInput?.(inputRef.current);
      }
    };

    return (
      <StyledSearchBox
        searchBarStatus={searchBarStatus}
        width={props.width}
        css={props.css}
        sizeType={sizeType}
        rounded={props.rounded}
      >
        <input
          ref={inputRef}
          type="text"
          className="input-search"
          placeholder={props.placeholder}
          onFocus={handleOnFocusInput}
          onBlur={handleOnBlurInput}
          onInput={onInputValueChange}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.nativeEvent.isComposing) return;
            if (keymap.isEscape(e)) {
              inputRef.current?.blur();
            }
            setClearActive(true);
            props.onKeyDown?.(e);
          }}
          value={inputValue}
        />

        {searchBarStatus === 'expanded' ? (
          <>
            {clearActive && (
              <>
                <Button
                  className="btn-clear"
                  variant="ghost"
                  onClick={onClickClear}
                >
                  clear
                </Button>
                <Space w={10} />
              </>
            )}

            <CenterFlex
              className="btn-search"
              style={{
                fontSize: '16px',
                width: `${BUTTON_SIZE[sizeType]}px`,
                height: `${BUTTON_SIZE[sizeType]}px`,
              }}
            >
              <SearchOutlined />
            </CenterFlex>
          </>
        ) : (
          <IconButton
            className="btn-search"
            icon={<SearchOutlined />}
            sizeType={sizeType}
            outlined={false}
            tabIndex={-1}
            onClick={() => {
              inputRef.current?.focus();
            }}
          />
        )}
      </StyledSearchBox>
    );
  },
);

export default ExpandSearchInput;

type StyledSearchBoxProps = {
  searchBarStatus: SearchBarStatusT;
  width?: string;
  css?: StyledCss;
  sizeType: SizeType;
  rounded?: boolean;
};

const StyledSearchBox = styled(Flex).attrs({
  a: 'center',
})<StyledSearchBoxProps>`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: ${({ rounded }) => (rounded ? '24px' : '8px')};
  height: fit-content;
  width: ${({ searchBarStatus, width, sizeType }) =>
    searchBarStatus === 'expanded'
      ? width || '100%'
      : `${BUTTON_SIZE[sizeType]}px`};
  overflow: hidden;
  transition: all 0.3s;
  ${({ css }) => css && css};

  .btn-search {
    position: absolute;
    left: 0;
  }

  .btn-search:focus {
    outline: none;
  }

  .input-search {
    height: ${(props) => `${HeightSize[props.sizeType]}px`};
    border-style: none;
    outline: none;
    flex: 1;
    width: 0;
    transition: all 0.3s;
    padding: 0 10px 0 0;
    font-size: 14px;
    background: transparent;
    color: ${(props) => props.theme.colors.gray800};
    margin-left: 40px;
    &::placeholder {
      color: ${(props) => props.theme.colors.gray500};
    }
  }
`;
